import axios from "axios";
import { ERROR_MESSAGE } from "core-lib/constants/network";
import { checkMobileOS, isDataValid } from "core-lib/utils/helpers";
import { API_SECRET } from "../config/Env";
import errorHandler from "./error_handler";
import { genericErrorHandler } from "./genericErrorHandler";
import { addToCacheQueue, deleteDataFromCache, getCachedData } from "./idb";
import { getDeviceId, getNativeVersions, isParkPlusWebView } from "./source";
import { isServer } from "./utils";

export enum CacheStrategyEnum {
  NoStore = "NoStore",
  Cache = "Cache",
  CacheAndRefresh = "CacheAndRefresh",
}

/**
 * Optional cap on server-side (request-path SSR / getServerSideProps) request
 * timeouts, in ms. This lib is shared by every app in the monorepo, so the cap
 * is **opt-in and OFF by default** — an app is only affected if its deployment
 * sets `SSR_API_TIMEOUT_MS`. With it unset, behaviour is identical to before.
 *
 * Why: the edge gateway (Kong) / uptime checks give up on a request at ~15s. If
 * an SSR fetch hangs longer, the client has already disconnected (logged as a
 * 499) while Node keeps the connection open doing abandoned work — under load
 * this piles up and takes the whole service down (parkplus-website 499 flap).
 * Capping request-path SSR below the edge timeout makes a slow upstream fail
 * fast and fall back (pages already handle the error/empty response).
 *
 * Guards:
 *  - `isServer()` only — client (browser) calls always keep the caller timeout.
 *  - Skipped during `next build` — build-time getStaticProps is NOT behind the
 *    edge timeout and may legitimately take longer, so it must not be capped.
 *    (Runtime ISR revalidation still caps → serves stale on a slow upstream.)
 */
const SERVER_MAX_TIMEOUT = Number(process.env.SSR_API_TIMEOUT_MS) || 0;
const IS_BUILD_PHASE = process.env.NEXT_PHASE === "phase-production-build";

const capServerTimeout = (requested: number): number =>
  SERVER_MAX_TIMEOUT > 0 &&
  isServer() &&
  !IS_BUILD_PHASE &&
  requested > SERVER_MAX_TIMEOUT
    ? SERVER_MAX_TIMEOUT
    : requested;

type ApiTypes = {
  response?: { [key: string]: any };
  header?: { [key: string]: any };
  error?: string | { [key: string]: any };
  status?: number;
  toast?: any;
  timeout?: number;
  errorData?: { [key: string]: any };
};

export async function getMethod({
  endPoint,
  headers = { "Cache-Control": "no-cache" },
  params = null,
  enableErrorHandling = false,
  timeout = 50000,
  cacheStrategy = CacheStrategyEnum.NoStore,
  maxCacheAge = 24, // in hours
}: {
  endPoint: string;
  headers?: any;
  params?: { [key: string]: any };
  enableErrorHandling?: boolean;
  timeout?: number;
  cacheStrategy?: keyof typeof CacheStrategyEnum;
  maxCacheAge?: number;
}): Promise<ApiTypes> {
  timeout = capServerTimeout(timeout);
  const maxAge = maxCacheAge * 3600 * 1000;
  const url = new URL(endPoint);
  if (params) {
    for (let k in params) {
      url.searchParams.append(k, params[k]);
    }
  }

  const cacheKey = url.toString();

  // Check for cached data
  if (cacheStrategy !== CacheStrategyEnum.NoStore) {
    const cache = await getCachedData(cacheKey);
    if (cache) {
      const isExpired =
        new Date().getTime() - new Date(cache.timestamp).getTime() > maxAge;

      if (!isExpired) {
        // Trigger a background fetch without waiting for it
        if (cacheStrategy === CacheStrategyEnum.CacheAndRefresh) {
          fetchAndUpdateCache({
            endPoint,
            headers,
            params,
            timeout,
            cacheKey,
            cacheStrategy,
            enableErrorHandling,
          });
        }

        return cache?.data; // Immediately return cached data
      }

      // If expired, delete the cache entry
      await deleteDataFromCache(cacheKey);
    }
  }

  // Fetch fresh data directly if no valid cache is available
  return fetchAndUpdateCache({
    endPoint,
    headers,
    params,
    timeout,
    cacheKey,
    cacheStrategy,
    enableErrorHandling,
  });
}

// Function to fetch fresh data and update cache
async function fetchAndUpdateCache({
  endPoint,
  headers,
  params,
  timeout,
  cacheKey,
  cacheStrategy,
  enableErrorHandling,
}: {
  endPoint: string;
  headers: any;
  params: any;
  timeout: number;
  cacheKey: string;
  cacheStrategy: `${CacheStrategyEnum}`;
  enableErrorHandling?: boolean;
}): Promise<ApiTypes> {
  try {
    const response = await axios.get(`${endPoint}`, {
      headers,
      params,
      timeout,
    });
    const obj = {
      response: response?.data,
      header: response?.headers,
      toast: errorHandler(response),
      status: response?.status,
    };
    if (cacheStrategy !== CacheStrategyEnum.NoStore) {
      // Add to the queue for background processing
      addToCacheQueue(cacheKey, {
        data: obj,
        timestamp: new Date().toISOString(),
      });
    }

    return obj;
  } catch (error) {
    if (enableErrorHandling) {
      genericErrorHandler(error?.response);
    }
    return {
      error: error?.response?.data ?? ERROR_MESSAGE.genericFailure,
      header: error?.response?.headers,
      toast: errorHandler(error?.response),
      errorData: error?.response,
      status: error?.response?.status,
    };
  }
}

export function originalGetMethod(
  location,
  headers = { "Cache-Control": "no-cache" }
) {
  return axios
    .get(`${location}`, { headers })
    .then((response) =>
      // console.log('response', response);
      ({ response })
    )
    .catch((error) => {
      // console.log('error.response', error.response);
      return { error };
    });
}

export function originalPostMethod(location, body, headers = null) {
  let headerData = null;
  if (isDataValid(headers)) {
    headerData = { headers };
  }
  return axios
    .post(`${location}`, body, headerData)
    .then((response) =>
      // console.log('response', response);
      ({ response })
    )
    .catch((error) =>
      // handleUnAuthorized(error);
      // console.log('error.response', error.response);
      ({ error })
    );
}

export function postMethod({
  endPoint,
  body,
  headers = null,
  enableErrorHandling = false,
  timeout = 50000,
}: {
  endPoint: string;
  body: { [key: string]: any } | null;
  headers?: Headers | any;
  enableErrorHandling?: boolean;
  timeout?: number;
}): Promise<ApiTypes> {
  timeout = capServerTimeout(timeout);
  let headerData: any = null;
  let errorMessage = "";
  if (isDataValid(headers)) {
    if (headers?.has && headers.has("Content-Type")) {
      headerData = { headers };
    } else {
      headerData = {
        headers: { ...headers, "Content-Type": "application/json" },
      };
    }
  }

  return axios
    .post(`${endPoint}`, body, { ...headerData, timeout })
    .then((response) => ({
      response: response?.data,
      status: response?.status,
    }))
    .catch((error) => {
      if (enableErrorHandling) {
        genericErrorHandler(error?.response);
      }
      if (
        typeof error?.response?.data === "string" &&
        error?.response?.data === ""
      ) {
        errorMessage = ERROR_MESSAGE.genericFailure;
      } else {
        errorMessage = error?.response?.data;
      }
      return {
        error: errorMessage ?? ERROR_MESSAGE.genericFailure,
        status: error?.response?.status,
      };
    });
}

export function postMethodStream({
  endPoint,
  body,
  headers = null,
  signal,
}: {
  endPoint: string;
  body: { [key: string]: any } | null;
  headers?: any;
  signal?: AbortSignal;
}): Promise<Response> {
  const merged: Record<string, string> = {};
  if (isDataValid(headers)) {
    if (headers?.has && typeof headers.forEach === "function") {
      headers.forEach((value: string, key: string) => {
        merged[key] = value;
      });
    } else {
      Object.assign(
        merged,
        Object.fromEntries(
          Object.entries(headers).map(([k, v]) => [k, String(v)])
        )
      );
    }
  }
  const finalHeaders = { ...merged, "Content-Type": "application/json" };

  return fetch(endPoint, {
    method: "POST",
    headers: finalHeaders,
    body: body == null ? null : JSON.stringify(body),
    signal,
  });
}

export function putMethod({
  endPoint,
  body,
  headers = null,
  enableErrorHandling = false,
  timeout = 50000,
}: {
  endPoint: string;
  body: { [key: string]: any } | null;
  headers?: any;
  enableErrorHandling?: boolean;
  timeout?: number;
}): Promise<ApiTypes> {
  timeout = capServerTimeout(timeout);
  let headerData: any = null;
  let errorMessage = "";

  if (isDataValid(headers)) {
    headerData = {
      headers: { ...headers, "Content-Type": "application/json" },
    };
  }

  return axios
    .put(`${endPoint}`, body, { ...headerData, timeout })
    .then((response) => ({ response: response.data, status: response.status }))
    .catch((error) => {
      if (enableErrorHandling) {
        genericErrorHandler(error?.response);
      }
      if (
        typeof error?.response?.data === "string" &&
        error?.response?.data === ""
      ) {
        errorMessage = ERROR_MESSAGE.genericFailure;
      } else {
        errorMessage = error?.response?.data;
      }
      return {
        error: errorMessage ?? ERROR_MESSAGE.genericFailure,
      };
    });
}

export function patchMethod({
  endPoint,
  body,
  headers = null,
  enableErrorHandling = false,
  timeout = 50000,
}: {
  endPoint: string;
  body: { [key: string]: any } | null;
  headers?: any;
  enableErrorHandling?: boolean;
  timeout?: number;
}): Promise<ApiTypes> {
  timeout = capServerTimeout(timeout);
  let headerData: any = null;
  let errorMessage = "";

  if (isDataValid(headers)) {
    headerData = { headers };
  }

  return axios
    .patch(`${endPoint}`, body, { ...headerData, timeout })
    .then((response) => ({ response: response.data, status: response.status }))
    .catch((error) => {
      if (enableErrorHandling) {
        genericErrorHandler(error?.response);
      }
      if (
        typeof error?.response?.data === "string" &&
        error?.response?.data === ""
      ) {
        errorMessage = ERROR_MESSAGE.genericFailure;
      } else {
        errorMessage = error?.response?.data;
      }
      return {
        error: errorMessage ?? ERROR_MESSAGE.genericFailure,
      };
    });
}

export function deleteMethod({
  endPoint,
  body = null,
  headers = { "Content-Type": "application/json" },
  enableErrorHandling = false,
  timeout = 50000, // Default timeout of 50 seconds
}: {
  endPoint: string;
  body?: { [key: string]: any } | null;
  headers?: { [key: string]: any } | null;
  enableErrorHandling?: boolean;
  timeout?: number;
}): Promise<ApiTypes> {
  timeout = capServerTimeout(timeout);
  let errorMessage = "";
  let headerData: any = null;

  if (isDataValid(headers)) {
    headerData = { ...headers };
  }

  const data = { ...body };

  return axios
    .delete(`${endPoint}`, { data, headers: headerData, timeout })
    .then((response) => ({ response: response.data, status: response.status }))
    .catch((error) => {
      if (enableErrorHandling) {
        genericErrorHandler(error?.response);
      }
      if (
        typeof error?.response?.data === "string" &&
        error?.response?.data === ""
      ) {
        errorMessage = ERROR_MESSAGE.genericFailure;
      } else {
        errorMessage = error?.response?.data;
      }
      return {
        error: errorMessage ?? ERROR_MESSAGE.genericFailure,
      };
    });
}

export function setAuthHeaders(token) {
  token && (axios.defaults.headers.common.Authorization = token);
}

export function removeAuthHeaders() {
  delete axios.defaults.headers.common["Authorization"];
}

export function setClientHeaders(
  options = null,
  clientId = API_SECRET.clientId,
  clientSecret = API_SECRET.clientSecret
) {
  axios.defaults.headers.common["client-id"] = clientId;
  axios.defaults.headers.common["client-secret"] = clientSecret;
  axios.defaults.headers.common.platform = "web";
  axios.defaults.headers.common["package-name"] = "web.pwa";
  if (!isServer()) {
    const isPPWebview = isParkPlusWebView();
    axios.defaults.headers.common["app-name"] = isPPWebview
      ? "B2C"
      : "Park+ PWA";
    if (isPPWebview) {
      axios.defaults.headers.common["native-platform"] = checkMobileOS();
    }
  }

  const { appVersion, osVersion } = getNativeVersions(options);
  if (appVersion) {
    axios.defaults.headers.common["version-name"] = appVersion;
  }
  if (osVersion) {
    axios.defaults.headers.common["os-version"] = osVersion;
  }
  const deviceId = getDeviceId(options);
  if (deviceId) {
    axios.defaults.headers.common["device-id"] = deviceId;
  }
  axios.defaults.timeout = 50000;
}
